/**
 * Comparison view for the med-dictate live benchmark: per-model summary, WER bar chart,
 * a sample x model WER table, and a word-level diff (errors highlighted) for the
 * selected cell.
 */
import React, { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { diffWords } from "diff";
import { WER_CATEGORY_COLORS } from "../utils/werCalculator";

const axisColor = "#cbd5f5";
const gridColor = "rgba(148, 163, 184, 0.2)";
const tooltipStyles = {
  backgroundColor: "#0f172a",
  borderRadius: "0.75rem",
  border: "1px solid rgba(255,255,255,0.1)",
  color: "#e2e8f0",
};

const panelClass =
  "rounded-2xl border border-white/10 bg-white/5 backdrop-blur-2xl shadow-[0_25px_80px_rgba(2,6,23,0.55)] p-6";

const MedDictateBenchmarkResults = ({ results }) => {
  const [selectedCell, setSelectedCell] = useState(null);

  const { modelIds, sampleIds, byModel, cellIndex } = useMemo(() => {
    const modelSet = new Set();
    const sampleSet = new Set();
    const byModelAcc = {};
    const index = {};

    results.forEach((row) => {
      modelSet.add(row.modelId);
      sampleSet.add(row.sampleId);
      if (!byModelAcc[row.modelId]) byModelAcc[row.modelId] = [];
      byModelAcc[row.modelId].push(row.wer);
      index[`${row.sampleId}::${row.modelId}`] = row;
    });

    return {
      modelIds: Array.from(modelSet),
      sampleIds: Array.from(sampleSet),
      byModel: byModelAcc,
      cellIndex: index,
    };
  }, [results]);

  const chartData = modelIds.map((modelId) => {
    const values = byModel[modelId];
    const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
    return { model: modelId, averageWER: parseFloat(avg.toFixed(2)) };
  });

  const selectedRow = selectedCell ? cellIndex[selectedCell] : null;
  const diffParts = selectedRow
    ? diffWords(
        selectedRow.referenceText || "",
        selectedRow.hypothesisText || "",
      )
    : [];

  return (
    <div className={panelClass}>
      <h3 className="text-xl font-semibold text-white">
        Comparaison des modeles
      </h3>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {modelIds.map((modelId) => {
          const values = byModel[modelId];
          const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
          return (
            <div
              key={modelId}
              className="rounded-xl border border-white/10 bg-slate-950/40 p-4"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                {modelId}
              </p>
              <p className="mt-2 text-2xl font-semibold text-white">
                {avg.toFixed(2)}%
              </p>
              <p className="text-xs text-slate-400">
                WER moyen sur {values.length} extrait(s)
              </p>
            </div>
          );
        })}
      </div>

      <div className="mt-6 h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="model" stroke={axisColor} tick={{ fontSize: 11 }} />
            <YAxis stroke={axisColor} unit="%" />
            <Tooltip
              contentStyle={tooltipStyles}
              formatter={(value) => [`${value}%`, "WER moyen"]}
            />
            <Bar dataKey="averageWER" fill="#38bdf8" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-6 overflow-x-auto">
        <table className="w-full text-left text-sm text-slate-200">
          <thead>
            <tr>
              <th className="p-2 text-xs uppercase tracking-[0.2em] text-slate-400">
                Extrait
              </th>
              {modelIds.map((modelId) => (
                <th
                  key={modelId}
                  className="p-2 text-xs uppercase tracking-[0.2em] text-slate-400"
                >
                  {modelId}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sampleIds.map((sampleId) => (
              <tr key={sampleId} className="border-t border-white/5">
                <td className="p-2 font-medium text-white">{sampleId}</td>
                {modelIds.map((modelId) => {
                  const key = `${sampleId}::${modelId}`;
                  const row = cellIndex[key];
                  if (!row)
                    return (
                      <td key={modelId} className="p-2 text-slate-500">
                        —
                      </td>
                    );
                  return (
                    <td key={modelId} className="p-2">
                      <button
                        type="button"
                        onClick={() => setSelectedCell(key)}
                        className="rounded-lg px-2 py-1 font-semibold"
                        style={{
                          backgroundColor: `${WER_CATEGORY_COLORS[row.category]}22`,
                          color: WER_CATEGORY_COLORS[row.category],
                        }}
                      >
                        {row.wer}%
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedRow && (
        <div className="mt-6 rounded-xl border border-white/10 bg-slate-950/40 p-4">
          <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
            {selectedRow.sampleId} — {selectedRow.modelId} — WER{" "}
            {selectedRow.wer}%
          </p>
          <p className="mt-3 text-sm leading-relaxed text-slate-200">
            {diffParts.map((part, index) => (
              <span
                key={index}
                className={
                  part.removed
                    ? "bg-red-500/30 text-red-200 line-through"
                    : part.added
                      ? "bg-emerald-500/30 text-emerald-200 underline"
                      : ""
                }
              >
                {part.value}
              </span>
            ))}
          </p>
        </div>
      )}
    </div>
  );
};

export default MedDictateBenchmarkResults;
